import type { Patch } from "../engine/types";
import type { LooperProject, PaletteEntry, Cell, Meter } from "./types";

function patch(name: string, over: Partial<Patch> & { oscillator?: Partial<Patch["oscillator"]>; filter?: Partial<Patch["filter"]>; ampEnv?: Partial<Patch["ampEnv"]> }): Patch {
  return {
    version: 1,
    name,
    oscillator: { type: "sawtooth", detune: 0, pulseWidth: 0.5, ...over.oscillator },
    filter: { type: "lowpass", cutoff: 3000, q: 2, ...over.filter },
    ampEnv: { attack: 0.005, decay: 0.15, sustain: 0.0, release: 0.15, ...over.ampEnv },
    lfo: { target: "off", shape: "sine", rate: 5, depth: 0 },
    masterGain: 0.8,
  };
}

// A small starter palette of distinct, useful voices.
export const BUILTIN_PALETTE: PaletteEntry[] = [
  { id: "sub", patch: patch("Sub Bass", { oscillator: { type: "triangle", detune: 0, pulseWidth: 0.5 }, filter: { type: "lowpass", cutoff: 600, q: 1 }, ampEnv: { attack: 0.01, decay: 0.3, sustain: 0.4, release: 0.2 } }) },
  { id: "pluck", patch: patch("Pluck", { oscillator: { type: "pulse", detune: 0, pulseWidth: 0.35 }, filter: { type: "lowpass", cutoff: 2600, q: 6 }, ampEnv: { attack: 0.002, decay: 0.18, sustain: 0, release: 0.12 } }) },
  { id: "pad", patch: patch("Pad", { oscillator: { type: "sawtooth", detune: 6, pulseWidth: 0.5 }, filter: { type: "lowpass", cutoff: 1800, q: 1 }, ampEnv: { attack: 0.25, decay: 0.4, sustain: 0.7, release: 0.6 } }) },
  { id: "blip", patch: patch("Blip", { oscillator: { type: "square", detune: 0, pulseWidth: 0.5 }, filter: { type: "highpass", cutoff: 400, q: 1 }, ampEnv: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.05 } }) },
];

// Brand cyan/purple only — box chrome is one accent anyway, so these now just
// tint the notes in the editor.
const COLORS = ["#00bfff", "#b388ff", "#3ad6ff", "#8a6fd0"];

function emptyCell(id: string, name: string, meter: Cell["meter"], beats: number, color: string): Cell {
  return { id, name, color, meter, lengthBeats: beats, hits: [], transpose: 0, speed: 1, gain: 0.8, muted: false };
}

export function makeDefaultProject(): LooperProject {
  return {
    version: 1,
    name: "Untitled",
    bpm: 100,
    palette: structuredClone(BUILTIN_PALETTE),
    cells: [
      emptyCell("cell-1", "Box 1", "4/4", 4, COLORS[0]),
      emptyCell("cell-2", "Box 2", "4/4", 4, COLORS[1]),
      emptyCell("cell-3", "Box 3", "3/4", 3, COLORS[2]),
    ],
  };
}

const METER_BEATS: Record<Meter, number> = { "4/4": 4, "3/4": 3, free: 4 };

let counter = 0;
export function newCell(meter: Meter = "4/4"): Cell {
  counter += 1;
  return emptyCell(`cell-${Date.now()}-${counter}`, `Box ${counter + 3}`, meter, METER_BEATS[meter], COLORS[counter % COLORS.length]);
}
